// Libs
import { getAuth, getIdToken } from "firebase/auth";
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  query,
  where,
  Timestamp,
  getDoc,
  getDocs,
  orderBy,
} from "firebase/firestore";

// Local
import axios from "axios";
import {
  ExistingVendor,
  Vendor_CreateAPI,
  Vendor_UpdateAPI,
  VendorManager,
} from "../models/vendor";
import {
  ExistingPurchaseOrder,
  PO_CreateAPI,
  PO_UpdateAPI,
  POStatusValues,
  PurchaseOrderManager,
} from "../models/purchase-order";
import {
  ExistingPurchaseOrderReceipt,
  PurchaseOrderReceipt_CreateAPI,
  PurchaseOrderReceipt_UpdateAPI,
  PurchaseOrderReceiptManager,
} from "../models/purchase-order-receipt";
import { logger as devLogger } from "../logging";

const projectName = import.meta.env.VITE_APP_FIREBASE_PROJECT;
let apiBaseURL = `https://us-central1-${projectName}.cloudfunctions.net/api`;
if (import.meta.env.VITE_APP_USE_FUNCTIONS_EMULATOR === "true") {
  apiBaseURL = `http://localhost:5001/${projectName}/us-central1/api`;
}

export const VendorPOAPI = {
  // Reads
  getAllVendors: getAllVendors,
  getVendor: getVendor,
  subscribeAllVendors: subscribeAllVendors,
  subscribeSingleVendor: subscribeSingleVendor,
  getAllPurchaseOrders: getAllPurchaseOrders,
  getPurchaseOrder: getPurchaseOrder,
  getPurchaseOrderPDF: getPurchaseOrderPDF,
  subscribeAllPurchaseOrders: subscribeAllPurchaseOrders,
  subscribeSinglePurchaseOrder: subscribeSinglePurchaseOrder,
  subscribeAllPurchaseOrdersByCraftRecordID:
    subscribeAllPurchaseOrdersByCraftRecordID,
  getAllPurchaseOrderReceipts: getAllPurchaseOrderReceipts,
  getPurchaseOrderReceiptsByPurchaseOrderId:
    getPurchaseOrderReceiptsByPurchaseOrderId,
  subscribeAllPurchaseOrderReceipts: subscribeAllPurchaseOrderReceipts,
  subscribePurchaseOrderReceiptsByPurchaseOrderId:
    subscribePurchaseOrderReceiptsByPurchaseOrderId,
  // Writes & Actions
  createVendor: createVendor,
  updateVendor: updateVendor,
  deleteVendor: deleteVendor,
  createPurchaseOrder: createPurchaseOrder,
  updatePurchaseOrder: updatePurchaseOrder,
  deletePurchaseOrder: deletePurchaseOrder,
  emailPurchaseOrder: emailPurchaseOrder,
  createPurchaseOrderReceipt: createPurchaseOrderReceipt,
  updatePurchaseOrderReceipt: updatePurchaseOrderReceipt,
  deletePurchaseOrderReceipt: deletePurchaseOrderReceipt,
};

// =========== VENDOR READ FUNCTIONS ===========
async function getAllVendors(siteKey: string): Promise<ExistingVendor[]> {
  const db = getFirestore();
  const vendorsCollection = collection(db, `siteKeys/${siteKey}/vendors`);
  const q = query(vendorsCollection, where("deleted", "==", false));

  const vendors: ExistingVendor[] = [];
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    const vendor = VendorManager.createFromFirestoreSnapshot(doc);
    vendors.push(vendor);
  });
  return vendors;
}

function subscribeAllVendors(
  siteKey: string,
  callback: (vendors: ExistingVendor[]) => void,
): () => void {
  const db = getFirestore();
  const vendorsCollection = collection(db, `siteKeys/${siteKey}/vendors`);
  const q = query(vendorsCollection, where("deleted", "==", false));

  return onSnapshot(q, (querySnapshot) => {
    const vendors: ExistingVendor[] = [];
    querySnapshot.forEach((doc) => {
      try {
        const vendor = VendorManager.createFromFirestoreSnapshot(doc);
        vendors.push(vendor);
      } catch (e) {
        console.error("Error creating vendor from snapshot", e);
      }
    });
    callback(vendors);
  });
}

async function getVendor(
  siteKey: string,
  vendorID: string,
): Promise<ExistingVendor | null> {
  const db = getFirestore();
  const vendorRef = doc(db, `siteKeys/${siteKey}/vendors/${vendorID}`);

  const docSnapshot = await getDoc(vendorRef);
  if (docSnapshot.exists()) {
    return VendorManager.createFromFirestoreSnapshot(docSnapshot);
  }
  return null;
}

function subscribeSingleVendor(
  siteKey: string,
  vendorID: string,
  callback: (vendor: ExistingVendor | null) => void,
): () => void {
  const db = getFirestore();
  const vendorRef = doc(db, `siteKeys/${siteKey}/vendors/${vendorID}`);

  return onSnapshot(vendorRef, (docSnapshot) => {
    if (docSnapshot.exists()) {
      try {
        const vendor = VendorManager.createFromFirestoreSnapshot(docSnapshot);
        callback(vendor);
      } catch (e) {
        console.error("Error creating vendor from snapshot", e);
        callback(null);
      }
    } else {
      callback(null);
    }
  });
}

// =========== PURCHASE ORDER READ FUNCTIONS ===========
async function getAllPurchaseOrders(
  siteKey: string,
): Promise<ExistingPurchaseOrder[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = `purchase-orders/${siteKey}`;
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    const response = await axios.get(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
    return response.data.purchaseOrders;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

function subscribeAllPurchaseOrders(
  siteKey: string,
  startDate: Date | null = null,
  endDate: Date | null = null,
  statusList: POStatusValues[] | null = null,
  callback: (purchaseOrders: ExistingPurchaseOrder[]) => void,
): () => void {
  const db = getFirestore();
  const purchaseOrdersCollection = collection(
    db,
    `siteKeys/${siteKey}/purchaseOrders`,
  );

  let q = query(purchaseOrdersCollection);

  // Add the date range filter if dates are provided
  if (startDate && endDate) {
    q = query(
      q,
      where("issueDate", ">=", Timestamp.fromDate(startDate)),
      where("issueDate", "<=", Timestamp.fromDate(endDate)),
    );
  }

  // Add status filter if status list is provided
  if (statusList && statusList.length > 0) {
    q = query(q, where("status", "in", statusList));
  }

  // Sort by issueDate with most recent first
  q = query(q, orderBy("issueDate", "desc"));

  return onSnapshot(q, (querySnapshot) => {
    const purchaseOrders: ExistingPurchaseOrder[] = [];
    querySnapshot.forEach((doc) => {
      try {
        const purchaseOrder =
          PurchaseOrderManager.createFromFirestoreSnapshot(doc);
        purchaseOrders.push(purchaseOrder);
      } catch (e) {
        console.error("Error creating purchase order from snapshot", e);
      }
    });
    callback(purchaseOrders);
  });
}

function subscribeAllPurchaseOrdersByCraftRecordID(
  siteKey: string,
  craftRecordID: string,
  callback: (purchaseOrders: ExistingPurchaseOrder[]) => void,
): () => void {
  const db = getFirestore();
  const purchaseOrdersCollection = collection(
    db,
    `siteKeys/${siteKey}/purchaseOrders`,
  );
  const q = query(
    purchaseOrdersCollection,
    where("craftRecordID", "==", craftRecordID),
  );

  return onSnapshot(q, (querySnapshot) => {
    const purchaseOrders: ExistingPurchaseOrder[] = [];
    querySnapshot.forEach((doc) => {
      try {
        const purchaseOrder =
          PurchaseOrderManager.createFromFirestoreSnapshot(doc);
        purchaseOrders.push(purchaseOrder);
      } catch (e) {
        console.error("Error creating purchase order from snapshot", e);
      }
    });
    callback(purchaseOrders);
  });
}

async function getPurchaseOrder(
  siteKey: string,
  purchaseOrderID: string,
): Promise<ExistingPurchaseOrder | null> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = `purchase-orders/${siteKey}/${purchaseOrderID}`;
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    const response = await axios.get(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
    return response.data.purchaseOrder;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 404) {
        return null;
      }
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function getPurchaseOrderPDF(
  siteKey: string,
  purchaseOrderID: string,
): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = `purchase-order/pdf?siteKeyID=${siteKey}&poID=${purchaseOrderID}`;
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    const response = await axios.get(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

function subscribeSinglePurchaseOrder(
  siteKey: string,
  purchaseOrderID: string,
  callback: (purchaseOrder: ExistingPurchaseOrder | null) => void,
): () => void {
  const db = getFirestore();
  const purchaseOrderRef = doc(
    db,
    `siteKeys/${siteKey}/purchaseOrders/${purchaseOrderID}`,
  );

  return onSnapshot(purchaseOrderRef, (docSnapshot) => {
    if (docSnapshot.exists()) {
      try {
        const purchaseOrder =
          PurchaseOrderManager.createFromFirestoreSnapshot(docSnapshot);
        callback(purchaseOrder);
      } catch (e) {
        console.error("Error creating purchase order from snapshot", e);
        callback(null);
      }
    } else {
      callback(null);
    }
  });
}

// =========== PURCHASE ORDER RECEIPT READ FUNCTIONS ===========
async function getAllPurchaseOrderReceipts(
  siteKey: string,
): Promise<ExistingPurchaseOrderReceipt[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = `purchase-order-receipts/${siteKey}`;
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    const response = await axios.get(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
    return response.data.purchaseOrderReceipts;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function getPurchaseOrderReceiptsByPurchaseOrderId(
  siteKey: string,
  purchaseOrderID: string,
): Promise<ExistingPurchaseOrderReceipt[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = `purchase-orders/${siteKey}/${purchaseOrderID}/receipts`;
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    const response = await axios.get(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
    return response.data.receipts;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

function subscribeAllPurchaseOrderReceipts(
  siteKey: string,
  callback: (receipts: ExistingPurchaseOrderReceipt[]) => void,
): () => void {
  const db = getFirestore();
  const receiptsCollection = collection(
    db,
    `siteKeys/${siteKey}/purchaseOrderReceipts`,
  );
  const q = query(receiptsCollection, where("deleted", "==", false));

  return onSnapshot(q, (querySnapshot) => {
    const receipts: ExistingPurchaseOrderReceipt[] = [];
    querySnapshot.forEach((doc) => {
      try {
        const receipt =
          PurchaseOrderReceiptManager.createFromFirestoreSnapshot(doc);
        receipts.push(receipt);
      } catch (e) {
        console.error("Error creating purchase order receipt from snapshot", e);
      }
    });
    callback(receipts);
  });
}

function subscribePurchaseOrderReceiptsByPurchaseOrderId(
  siteKey: string,
  purchaseOrderID: string,
  callback: (receipts: ExistingPurchaseOrderReceipt[]) => void,
): () => void {
  const db = getFirestore();
  const receiptsCollection = collection(
    db,
    `siteKeys/${siteKey}/purchaseOrderReceipts`,
  );
  const q = query(
    receiptsCollection,
    where("purchaseOrderID", "==", purchaseOrderID),
    where("deleted", "==", false),
  );

  return onSnapshot(q, (querySnapshot) => {
    const receipts: ExistingPurchaseOrderReceipt[] = [];
    querySnapshot.forEach((doc) => {
      try {
        const receipt =
          PurchaseOrderReceiptManager.createFromFirestoreSnapshot(doc);
        receipts.push(receipt);
      } catch (e) {
        console.error("Error creating purchase order receipt from snapshot", e);
      }
    });
    callback(receipts);
  });
}

// =========== VENDOR WRITE FUNCTIONS ===========
async function createVendor(vendorDoc: Vendor_CreateAPI): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "vendor";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, vendorDoc, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function updateVendor(partialVendor: Vendor_UpdateAPI): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "vendor";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.put(fullApiRoute, partialVendor, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function deleteVendor(
  siteKey: string,
  vendorID: ExistingVendor["id"],
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = `vendor/${siteKey}/${vendorID}`;
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.delete(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

// =========== PURCHASE ORDER WRITE FUNCTIONS ===========
async function createPurchaseOrder(poDoc: PO_CreateAPI): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "purchase-order";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  // Convert issueDate and expectedDeliveryDate to ISO format
  const newPoDoc = {
    ...poDoc,
    issueDate: poDoc.issueDate.toDate().toISOString(),
    expectedDeliveryDate:
      poDoc.expectedDeliveryDate?.toDate().toISOString() ?? null,
  };

  try {
    await axios.post(fullApiRoute, newPoDoc, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function updatePurchaseOrder(partialPO: PO_UpdateAPI): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "purchase-order";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  // Convert date fields to ISO format if they exist
  const updatedPODoc = { ...partialPO };

  // Convert issueDate if it exists and is a Timestamp
  if (
    updatedPODoc.issueDate &&
    typeof updatedPODoc.issueDate === "object" &&
    "toDate" in updatedPODoc.issueDate
  ) {
    updatedPODoc.issueDate = updatedPODoc.issueDate
      .toDate()
      .toISOString() as any;
  }

  // Convert expectedDeliveryDate if it exists and is a Timestamp
  if (
    updatedPODoc.expectedDeliveryDate &&
    typeof updatedPODoc.expectedDeliveryDate === "object" &&
    "toDate" in updatedPODoc.expectedDeliveryDate
  ) {
    updatedPODoc.expectedDeliveryDate = updatedPODoc.expectedDeliveryDate
      .toDate()
      .toISOString() as any;
  }

  try {
    await axios.put(fullApiRoute, updatedPODoc, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function deletePurchaseOrder(
  siteKey: string,
  purchaseOrderID: ExistingPurchaseOrder["id"],
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = `purchase-order/${siteKey}/${purchaseOrderID}`;
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.delete(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function emailPurchaseOrder(
  siteKey: string,
  purchaseOrderID: ExistingPurchaseOrder["id"],
  emails: string[],
  additionalMessage: string,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "purchase-order/email";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  const data = {
    siteKeyID: siteKey,
    purchaseOrderID: purchaseOrderID,
    emails: emails,
    additionalMessage: additionalMessage,
  };

  try {
    await axios.post(fullApiRoute, data, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

// =========== PURCHASE ORDER RECEIPT WRITE FUNCTIONS ===========
async function createPurchaseOrderReceipt(
  receiptDoc: PurchaseOrderReceipt_CreateAPI,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "purchase-order-receipt";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, receiptDoc, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function updatePurchaseOrderReceipt(
  partialReceipt: PurchaseOrderReceipt_UpdateAPI,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "purchase-order-receipt";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  // Convert date fields to ISO format if they exist
  const updatedReceiptDoc = { ...partialReceipt };

  // Convert receivedDate if it exists and is a Timestamp
  if (
    updatedReceiptDoc.receivedDate &&
    typeof updatedReceiptDoc.receivedDate === "object" &&
    "toDate" in updatedReceiptDoc.receivedDate
  ) {
    updatedReceiptDoc.receivedDate = updatedReceiptDoc.receivedDate
      .toDate()
      .toISOString() as any;
  }

  try {
    await axios.put(fullApiRoute, updatedReceiptDoc, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

async function deletePurchaseOrderReceipt(
  receiptId: string,
  siteKey: string,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "purchase-order-receipt";
  const fullApiRoute = `${apiBaseURL}/${endpoint}?receiptID=${receiptId}&siteKeyID=${siteKey}`;

  try {
    await axios.delete(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      devLogger.info(err.response?.data);
      devLogger.info(err.response?.status);
      devLogger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}
